/*
  ChunkyFit landing pages — all LP behaviour in one deferred file.

  PRICING MODEL: prices shown here are DISPLAY ONLY. Kite runs automatically in
  the background and applies the real volume discount at checkout. The per-box
  prices in the section settings must be kept in sync with the Kite rules, or
  the page will promise a number the checkout does not honour.

  CART: we add one line item per flavour (real 10-pack products), so fulfilment
  reads normal SKUs with no line-item properties to interpret. The only property
  attached is the hidden _cf_lp tag, used to decide whether clearing the cart is
  safe.
*/
(function () {
  'use strict';

  var money = function (cents, fmt) {
    var n = (cents / 100).toFixed(2);
    return (fmt || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, n)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
  };

  /* ── tracking ───────────────────────────────────────────────── */
  function track(name, params) {
    params = params || {};
    try { if (window.fbq) window.fbq('track', name, params); } catch (e) {}
    try { if (window.ttq && window.ttq.track) window.ttq.track(name, params); } catch (e) {}
    try { if (window.gtag) window.gtag('event', name, params); } catch (e) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params));
    } catch (e) {}
  }
  function trackCustom(name, params) {
    params = params || {};
    try { if (window.fbq) window.fbq('trackCustom', name, params); } catch (e) {}
    try { if (window.gtag) window.gtag('event', name, params); } catch (e) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params));
    } catch (e) {}
  }

  /* ── scroll depth 25/50/75/100 ──────────────────────────────── */
  function scrollDepth() {
    var hit = {}, marks = [25, 50, 75, 100], ticking = false;
    function check() {
      ticking = false;
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      var pct = max > 0 ? Math.round((window.scrollY / max) * 100) : 100;
      marks.forEach(function (m) {
        if (pct >= m && !hit[m]) { hit[m] = true; trackCustom('lp_scroll_depth', { percent: m }); }
      });
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(check); }
    }, { passive: true });
    check();
  }

  /* ── outbound CTAs ──────────────────────────────────────────────
     A pre-lander only works if attribution survives the hop to the PDP,
     so copy this page's query string onto every outbound CTA. Existing
     params on the link win, so hand-set UTMs are never clobbered. */
  function forwardParams() {
    var qs = window.location.search;
    if (!qs || qs.length < 2) return;
    var src;
    try { src = new URLSearchParams(qs); } catch (e) { return; }

    document.querySelectorAll('a[data-lp-out][href]').forEach(function (a) {
      try {
        var u = new URL(a.getAttribute('href'), window.location.origin);
        if (u.origin !== window.location.origin) return;
        src.forEach(function (v, k) { if (!u.searchParams.has(k)) u.searchParams.set(k, v); });
        a.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (e) {}
    });
  }

  function outboundClicks() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[data-lp-out]');
      if (!a) return;
      trackCustom('lp_cta_click', {
        location: a.dataset.ctaLocation || 'body',
        destination: a.getAttribute('href') || ''
      });
    });
  }

  /* ── smooth anchors ─────────────────────────────────────────── */
  function anchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (id.length < 2) return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (id === '#offer') trackCustom('lp_cta_click', { location: a.dataset.ctaLocation || 'body' });
    });
  }

  /* ── sticky CTA: shows past the hero, hides once the offer is in view ── */
  function stickyBar() {
    var bar = document.querySelector('[data-lp-sticky]');
    if (!bar) return;
    var hero = document.querySelector('[data-lp-hero-end]') || document.querySelector('.lp-hero');
    var offer = document.getElementById('offer');
    var pastHero = false, offerVisible = false;

    function apply() {
      if (pastHero && !offerVisible) bar.setAttribute('data-visible', '');
      else bar.removeAttribute('data-visible');
    }
    if ('IntersectionObserver' in window) {
      if (hero) {
        new IntersectionObserver(function (es) {
          pastHero = !es[0].isIntersecting && es[0].boundingClientRect.top < 0;
          apply();
        }, { threshold: 0 }).observe(hero);
      } else { pastHero = true; }
      if (offer) {
        new IntersectionObserver(function (es) {
          offerVisible = es[0].isIntersecting;
          apply();
        }, { threshold: 0.08 }).observe(offer);
      }
    }
  }

  /* ── offer module ───────────────────────────────────────────── */
  function offerModule(root) {
    var dataEl = root.querySelector('[data-lp-offer-data]');
    if (!dataEl) return;
    var cfg;
    try { cfg = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var flavors = (cfg.flavors || []).filter(function (f) { return f.id; });
    var tiers = cfg.tiers || [];
    if (!flavors.length || !tiers.length) return;

    var countEl = root.querySelector('[data-lp-count]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-lp-card]'));
    var nowEl = root.querySelector('[data-lp-total-now]');
    var wasEl = root.querySelector('[data-lp-total-was]');
    var saveEl = root.querySelector('[data-lp-total-save]');
    var perEl = root.querySelector('[data-lp-total-per]');
    var shipEl = root.querySelector('[data-lp-ship]');
    var errEl = root.querySelector('[data-lp-error]');
    var btns = Array.prototype.slice.call(document.querySelectorAll('[data-lp-buy]'));
    var stickyPrice = document.querySelector('[data-lp-sticky-price]');
    cfg.strings.buy = btns.length ? btns[0].textContent.trim() : 'Complete purchase';

    // picks is a flat list of chosen variant ids, in the order the customer
    // tapped them. Its length is how many boxes they have chosen so far.
    var state = { tier: null, picks: [] };
    var busy = false;

    function currentTier() {
      return tiers.filter(function (t) { return String(t.boxes) === String(state.tier); })[0] || tiers[0];
    }

    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg;
      errEl.setAttribute('data-show', '');
    }
    function clearError() {
      if (errEl) errEl.removeAttribute('data-show');
    }

    function byId(id) {
      return flavors.filter(function (f) { return String(f.id) === String(id); })[0] || null;
    }
    function boxCount() {
      return parseInt(currentTier().boxes, 10) || 1;
    }
    function isComplete() {
      return state.picks.length === boxCount();
    }

    function renderPicker() {
      var max = boxCount();
      var n = state.picks.length;
      var full = n >= max;

      var counts = {};
      state.picks.forEach(function (id) { counts[id] = (counts[id] || 0) + 1; });

      cards.forEach(function (card) {
        var id = card.dataset.flavorId;
        var qty = counts[id] || 0;
        var qtyEl = card.querySelector('[data-lp-qty]');

        card.classList.toggle('is-picked', qty > 0);
        card.classList.toggle('is-full', full);
        if (qtyEl) qtyEl.textContent = qty > 1 ? String(qty) : '';

        var add = card.querySelector('[data-lp-add]');
        if (add && !card.classList.contains('is-oos')) {
          add.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false');
        }
      });

      if (countEl) {
        countEl.textContent = full
          ? (cfg.strings.countDone || 'All {max} boxes chosen').replace('{max}', max)
          : (cfg.strings.count || '{n} of {max} boxes chosen').replace('{n}', n).replace('{max}', max);
        countEl.classList.toggle('is-done', full);
      }

      btns.forEach(function (b) {
        if (b.dataset.busy) return;
        b.disabled = !full;
        b.textContent = full
          ? (cfg.strings.buy || 'Complete purchase')
          : (cfg.strings.buttonIncomplete || 'Choose your flavors');
      });
    }

    /* One rule, no dead taps: add a box while there is room, otherwise replace
       the box you added last. That makes the 1-box tier behave like a plain
       swap and the 3-box tier behave like "changed my mind about that one". */
    function addFlavor(id) {
      var f = byId(id);
      if (!f || !f.available) return;
      if (state.picks.length < boxCount()) state.picks.push(f.id);
      else state.picks[state.picks.length - 1] = f.id;
      clearError();
      renderPicker();
    }

    function removeFlavor(id) {
      for (var i = state.picks.length - 1; i >= 0; i--) {
        if (String(state.picks[i]) === String(id)) { state.picks.splice(i, 1); break; }
      }
      clearError();
      renderPicker();
    }

    cards.forEach(function (card) {
      var add = card.querySelector('[data-lp-add]');
      var minus = card.querySelector('[data-lp-minus]');
      if (add) add.addEventListener('click', function () {
        if (add.disabled) return;
        addFlavor(card.dataset.flavorId);
      });
      if (minus) minus.addEventListener('click', function (e) {
        e.stopPropagation();
        removeFlavor(card.dataset.flavorId);
      });
    });

    /* Changing tier keeps what they already picked, trimmed to the new size. */
    function renderSlots() {
      state.picks.length = Math.min(state.picks.length, boxCount());
      renderPicker();
    }

    function renderPrice() {
      var t = currentTier();
      var boxes = parseInt(t.boxes, 10) || 1;
      var now = Math.round(t.per_box_cents * boxes);
      var was = Math.round(cfg.msrp_cents * boxes);
      var saved = was - now;

      if (nowEl) nowEl.textContent = money(now, cfg.money_format);
      if (wasEl) {
        wasEl.textContent = saved > 0 ? money(was, cfg.money_format) : '';
        wasEl.style.display = saved > 0 ? '' : 'none';
      }
      if (saveEl) {
        saveEl.textContent = (cfg.strings.save || 'Save {amount}').replace('{amount}', money(saved, cfg.money_format));
        saveEl.style.display = saved > 0 ? '' : 'none';
      }
      if (perEl) {
        perEl.textContent = (cfg.strings.perBox || '{amount} per box')
          .replace('{amount}', money(t.per_box_cents, cfg.money_format))
          .replace('{cookies}', boxes * (cfg.cookies_per_box || 10));
      }
      if (shipEl && cfg.free_ship_cents) {
        var free = now >= cfg.free_ship_cents;
        shipEl.classList.toggle('lp-ship--locked', !free);
        shipEl.textContent = '';

        if (free) {
          shipEl.textContent = cfg.strings.shipFree || 'Free shipping included';
        } else {
          // Prefer nudging to the smallest bigger tier that clears the threshold.
          // Telling someone to add a box converts better than naming a dollar gap.
          var up = null;
          tiers.forEach(function (x) {
            var xb = parseInt(x.boxes, 10) || 1;
            if (xb <= boxes) return;
            if (x.per_box_cents * xb < cfg.free_ship_cents) return;
            if (!up || xb < (parseInt(up.boxes, 10) || 1)) up = x;
          });

          if (up && cfg.strings.shipLockedNext) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'lp-ship__up';
            btn.textContent = cfg.strings.shipLockedNext;
            btn.addEventListener('click', function () {
              var input = root.querySelector('[data-lp-tier][value="' + up.boxes + '"]');
              if (input) { input.checked = true; selectTier(up.boxes, true); }
            });
            shipEl.appendChild(btn);
          } else {
            shipEl.textContent = (cfg.strings.shipLocked || 'Add {amount} for free shipping')
              .replace('{amount}', money(cfg.free_ship_cents - now, cfg.money_format));
          }
        }
      }
      if (stickyPrice) stickyPrice.textContent = money(now, cfg.money_format);
    }

    function selectTier(boxes, fromUser) {
      state.tier = String(boxes);
      renderSlots();
      renderPrice();
      clearError();
      if (fromUser) trackCustom('lp_tier_select', { tier: state.tier, boxes: parseInt(boxes, 10) });
    }

    root.querySelectorAll('[data-lp-tier]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked) selectTier(input.value, true);
      });
    });

    /* ── add to cart, then straight to checkout ───────────────── */
    function buy() {
      if (busy) return;
      clearError();

      var t = currentTier();
      var boxes = parseInt(t.boxes, 10) || 1;

      if (!isComplete()) { renderPicker(); return; }

      // Aggregate duplicate flavours into a single line with quantity.
      var counts = {};
      for (var i = 0; i < state.picks.length; i++) {
        var f = byId(state.picks[i]);
        if (!f) { showError(cfg.strings.errVariant || 'That flavor is unavailable. Please pick another.'); return; }
        if (!f.available) {
          showError((cfg.strings.errStock || '{flavor} is out of stock. Please pick another.').replace('{flavor}', f.title));
          return;
        }
        counts[f.id] = (counts[f.id] || 0) + 1;
      }

      var items = Object.keys(counts).map(function (id) {
        return { id: parseInt(id, 10), quantity: counts[id], properties: { _cf_lp: cfg.lp_tag || 'listicle' } };
      });

      busy = true;
      btns.forEach(function (b) { b.dataset.busy = '1'; b.disabled = true; b.textContent = cfg.strings.loading || 'Working...'; });

      // Only clear when the cart already holds a lander line. Never wipe a cart
      // that came from somewhere else (and never wipe Navidium's protection line
      // unless we put a lander item there ourselves).
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : { items: [] }; })
        .then(function (cart) {
          var hasLander = (cart.items || []).some(function (l) {
            return l.properties && l.properties._cf_lp;
          });
          if (!hasLander) return null;
          return fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        })
        .catch(function () { return null; })
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: items })
          });
        })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (j) { throw new Error(j.description || j.message || 'add_failed'); });
          }
          return res.json();
        })
        .then(function () {
          var value = (t.per_box_cents * boxes) / 100;
          track('AddToCart', {
            value: value,
            currency: cfg.currency || 'USD',
            content_type: 'product',
            content_ids: items.map(function (i) { return String(i.id); }),
            num_items: boxes
          });
          // Give the pixel a beat to fire before we navigate away.
          setTimeout(function () { window.location.href = '/checkout'; }, 180);
        })
        .catch(function (err) {
          busy = false;
          btns.forEach(function (b) { delete b.dataset.busy; });
          renderPicker();
          var msg = String(err && err.message || '');
          if (/stock|available|sold/i.test(msg)) showError(cfg.strings.errStockGeneric || 'One of those flavors just sold out. Please pick another.');
          else showError(cfg.strings.errNetwork || 'Something went wrong. Please try again.');
        });
    }

    btns.forEach(function (b) { b.addEventListener('click', buy); });

    // Preselect the tier marked default in the theme editor.
    var checked = root.querySelector('[data-lp-tier]:checked');
    selectTier(checked ? checked.value : tiers[0].boxes, false);

    track('ViewContent', {
      content_type: 'product',
      content_name: cfg.lp_tag || 'listicle',
      content_ids: flavors.map(function (f) { return String(f.id); }),
      currency: cfg.currency || 'USD',
      value: (currentTier().per_box_cents * (parseInt(currentTier().boxes, 10) || 1)) / 100
    });
  }

  /* Native <dialog> so Esc, focus trapping and the backdrop come for free. */
  function nutritionDialogs() {
    document.querySelectorAll('[data-lp-nut]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var d = document.getElementById(btn.dataset.lpNut);
        if (!d) return;
        if (d.showModal) d.showModal(); else d.setAttribute('open', '');
        trackCustom('lp_nutrition_open', { flavor: (btn.closest('[data-lp-card]') || {}).dataset ? btn.closest('[data-lp-card]').dataset.flavorName : '' });
      });
    });

    document.querySelectorAll('.lp-nut').forEach(function (d) {
      var closer = d.querySelector('[data-lp-nut-close]');
      if (closer) closer.addEventListener('click', function () { d.close ? d.close() : d.removeAttribute('open'); });
      // Clicking the backdrop lands on the dialog element itself, not its panel.
      d.addEventListener('click', function (e) {
        if (e.target === d) { d.close ? d.close() : d.removeAttribute('open'); }
      });
    });
  }

  function init() {
    forwardParams();
    outboundClicks();
    anchors();
    nutritionDialogs();
    stickyBar();
    scrollDepth();
    document.querySelectorAll('[data-lp-offer]').forEach(offerModule);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();